<script lang="ts">
  import type { BlogCategoryStoryblok, BlogPostStoryblok } from '~/component-types-sb'; // TODO: probably should include this in tsconfig types instead (it is a d.ts file)

  import { page } from '$app/state';
  import { PUBLIC_BRAND_NAME } from '$env/static/public';
  import { getLocaleFromUrl, rewriteUrl } from '../../../../utils/locale.shared';

  import { storyblokEditable, getStoryblokApi } from "@storyblok/svelte";
  import { Section } from '@agapi/ui/section';
  import { Button } from '@agapi/ui/button';
  import { Loader } from '@agapi/ui/loader';
  import { Breadcrumb, BreadcrumbItem } from '@agapi/ui/breadcrumb';
  import { JsonLd } from '@agapi/ui/json-ld';
  import { Image } from '@agapi/ui/image';
  import { css } from '@agapi/ui/panda/css';
  import * as m from '$lib/parachute/messages';

  import type { BlogCardProps } from '../../../../components';
  import { BlogCard, BreadcrumbsSchema } from '../../../../components';
  import { blogPageStyles } from '../blog.styles';
  import { calculateReadingTime, parseIngress } from '../../../../utils/storyblok.client';
  import { getBlogSlug } from '../../../../utils/blog.shared';

  const { blok, posts, total, perPage, currentPage, slug }: {
    blok: BlogCategoryStoryblok;
    posts: any[];
    total: number;
    perPage: number;
    currentPage: number;
    slug: string;
  } = $props();



  let allPosts = $state(posts);
  let loading = $state(false);
  let currentPageState = $state(currentPage);
  const { language } = getLocaleFromUrl(page.url);
  const blogSlug = getBlogSlug(language);

  const loadMore = async () => {
    if (loading || allPosts?.length >= total) { return };

    loading = true;
    currentPageState += 1;
    const storyblokApi = getStoryblokApi();



    try {
      const { data } = await storyblokApi.get('cdn/stories', {
        version: 'published',
        starts_with: 'happy-green/magazine/posts/',
        is_startpage: false,
        per_page: perPage,
        language: language,
        page: currentPageState,
        filter_query: {
          category: {
            in: blok._uid
          }
        }
      });

      allPosts = [...allPosts, ...data.stories];
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      loading = false;
    }
  };


  const blogCardProps = (post: BlogPostStoryblok) => ({
    title: post.name,
    description: parseIngress(post.content.body.content),
    readingTime: calculateReadingTime(post.content.body.content),
    imageUrl: post.content.image?.filename,
    imageAlt: post.content.image?.alt,
    slug: post.slug,
    publishedAt: post.published_at,
    category: post.content.category[0]?.name || ''
  }) satisfies BlogCardProps;

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: blok.name,
    description: blok.description,
    url: `${page.url.origin}/magazine/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: posts.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${page.url.origin}/magazine/${post.slug}`
      }))
    }
  };

  const { section, sectionContainer, heading, subTitle, postGrid, loadMoreContainer } = blogPageStyles.raw();
</script>

<svelte:head>
  <title>{blok.name} - {PUBLIC_BRAND_NAME} {blogSlug}</title>
  <meta name="description" content={blok.description} />
  <link rel="canonical" href={`${page.url.origin}/${blogSlug}/${slug}`} />
  <meta property="og:title" content={blok.name} />
  <meta property="og:description" content={blok.description} />
  <meta property="og:image" content={blok.image?.filename} />
  <meta property="og:url" content={`${page.url.origin}/${blogSlug}/${slug}`} />
  <meta property="og:type" content="website" />
</svelte:head>


<BreadcrumbsSchema
  items={[
    { name: 'Magazine', item: '${page.url.origin}/magazine' },
    { name: blok.name ?? '', item: `${page.url.origin}/${blogSlug}/${blok.slug}` },
  ]}
/>

<JsonLd data={schemaData} />

<Section as="header" rootStyles={section} containerStyles={sectionContainer} use={[[storyblokEditable, blok]]}>
  <Breadcrumb>
    <BreadcrumbItem index={0}>
      <a href={rewriteUrl(page, '/blog')}>
        {m.blog()}
      </a>
    </BreadcrumbItem>
    <BreadcrumbItem index={1}>{blok.name}</BreadcrumbItem>
  </Breadcrumb>
  <h1 class={css(heading)}>{blok.name}</h1>
  <p class={css(subTitle)}>{blok.description}</p>

  {#if blok?.image && blok?.image?.filename !== ''}
    <Image
      width={800} aspectRatio={16 / 9}
      src={blok.image.filename ?? ''}
      alt={blok.image?.alt ?? blok.name ?? ''}
      css={{maxWidth: '100%', height: 'auto', marginBottom: '4'}} />
  {/if}

  </Section>
  <Section containerStyles={sectionContainer} rootStyles={section}>

  <div class={css(postGrid)}>
    {#each allPosts as post}
      <BlogCard {...blogCardProps(post)} />
    {/each}
  </div>

  {#if allPosts?.length < total}
    <div class={css(loadMoreContainer)}>
      <Button onclick={loadMore} disabled={loading}>
        {loading ? 'Laddar...' : 'Ladda fler'}
        {#if loading}
          <Loader />
        {/if}
      </Button>
    </div>
  {/if}
</Section>